import { Lock } from "lucide-react";
import { useId } from "react";

/**
 * GOVERNED OPTIONS — the /new screen's option cards.
 *
 * This is components/OptionCard.tsx with ONE structural change, and the change is the whole
 * point: the protected-fact promise is a property of the GROUP, not of the option.
 *
 * MEASURED ON THE LIVE APP: the empty /new form rendered TEN `.option-untouched` lock lines,
 * and four of them repeated the IDENTICAL sentence "Never adds partners, endorsements,
 * results or commitments." The primitive built to cure over-explaining had become the largest
 * single source of it — 14 instances of "Never" in 3,196 characters of body copy, one every
 * 228 characters. Nobody reads the eleventh restatement of a promise; they stop reading the
 * first.
 *
 * So the promise is stated ONCE per group, beneath the cards, and a card carries its own
 * `caveat` ONLY where it genuinely differs from the group promise (L3 alone, today). The
 * TYPE-LEVEL guarantee is untouched: `consequence` is still REQUIRED, so no governed option
 * can ever ship unexplained. The repetition goes; the guarantee stays.
 *
 * ALIGNMENT: the promise line's icon column is `var(--icon-sm)` with a `var(--space-3)` gap —
 * IDENTICAL to `.option-hit`'s radio column — so the promise's text edge lands on exactly the
 * same x as every option label's text edge. The old lock line used a --space-3 column with a
 * --space-2 gap, which put two icon columns in one card, 8px out of register with each other.
 *
 * NOTE FOR CONSOLIDATION: this should be folded back into components/OptionCard.tsx as a
 * `promise` prop on OptionGroup plus a `caveat` on OptionSpec. It lives here only because
 * /new is the sole consumer of OptionCard and the primitives layer is owned by another
 * workstream this round. The class names are deliberately unchanged.
 */

export type GovernedOption<T extends string> = {
  value: T;
  label: string;
  /** REQUIRED. One sentence: what selecting this will CHANGE. */
  consequence: string;
  /** One sentence, on this card only, when what it will NEVER touch differs from the group. */
  caveat?: string;
};

export function GovernedOptionGroup<T extends string>({
  label,
  help,
  hideLegend,
  promise,
  gate,
  value,
  options,
  onChange,
  columns
}: {
  label: string;
  help?: string;
  /**
   * Hide the legend FROM SIGHT ONLY — .sr-only, never `display: none`, so the fieldset keeps
   * its accessible name. Used where the numbered section head one line above already says the
   * identical word, and the legend would be the same noun printed twice.
   */
  hideLegend?: boolean;
  /** The standing promise for EVERY option in this group. Rendered once, under the cards. */
  promise: string;
  /**
   * WHAT THE GROUP GATES — a full-measure strip BELOW the grid, spanning both columns.
   *
   * It used to live INSIDE the L3 card, and that is what made the personalization row ragged:
   * the tallest, busiest card in a four-card grid was an UNSELECTED one, and row-stretch then
   * padded the selected L2 card out with ~114px of empty tint to match it. A card in a grid may
   * not be a container for a second, differently-shaped control.
   *
   * The gate is not weakened by moving: it is the same checkbox, bound to the same field, still
   * required before L3 may propose an argument change, still operable at any level (it always
   * was), and it now sits directly under the card it governs at a full measure instead of being
   * squeezed into a 300px column.
   */
  gate?: React.ReactNode;
  value: T;
  options: Array<GovernedOption<T>>;
  onChange: (value: T) => void;
  /** Grid columns at >=768. The phone is always one. */
  columns?: 2;
}) {
  const name = useId();
  return (
    <fieldset className="option-group" data-columns={columns || 1}>
      <legend className={hideLegend ? "sr-only" : undefined}>{label}</legend>
      {help ? <p className="option-group-help">{help}</p> : null}
      <div className="option-grid">
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <div
              className="option-card"
              data-testid="option-card"
              data-option-value={option.value}
              data-selected={selected ? "true" : "false"}
              key={option.value}
            >
              {/* A native radio gives arrow-key navigation, aria-checked and focus handling
                  for free, and it cannot desynchronise from the form state the way a
                  hand-rolled role=radio can. The whole label is the tap target. */}
              <label className="option-hit">
                <input
                  type="radio"
                  className="option-input"
                  name={name}
                  value={option.value}
                  checked={selected}
                  onChange={() => onChange(option.value)}
                />
                <span className="option-copy">
                  <span className="option-label">{option.label}</span>
                  <span className="option-consequence">{option.consequence}</span>
                  {option.caveat ? (
                    <span className="option-caveat">
                      <Lock aria-hidden="true" />
                      {option.caveat}
                    </span>
                  ) : null}
                </span>
              </label>
            </div>
          );
        })}
      </div>
      {/* The gate spans the grid, under the cards it governs. */}
      {gate}
      {/* Said ONCE, for the whole group. Its text edge sits on the option-label column. */}
      <p className="option-untouched">
        <Lock aria-hidden="true" />
        <span>{promise}</span>
      </p>
    </fieldset>
  );
}
